import { LEGACY_STORAGE_KEYS, STORAGE_KEYS } from '../constants/api.js'

const storageAvailable = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
const COOKIE_SESSION = 'cookie'
const TOKEN_SESSION = 'token'

const safeRead = (key) => {
  if (!storageAvailable()) return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const safeWrite = (key, value) => {
  if (!storageAvailable()) return
  try {
    window.localStorage.setItem(key, value)
  } catch {}
}

const safeRemove = (key) => {
  if (!storageAvailable()) return
  try {
    window.localStorage.removeItem(key)
  } catch {}
}

const safeJsonParse = (value) => {
  if (!value || value === 'undefined' || value === 'null') return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const migrateLegacyValue = (nextKey, legacyKey) => {
  const current = safeRead(nextKey)
  if (current) return current

  const legacy = safeRead(legacyKey)
  if (legacy) safeWrite(nextKey, legacy)
  return legacy
}

const getBase64UrlPayload = (token) => {
  const parts = token?.split('.')
  if (!parts?.[1]) return null

  const base64 = parts[1]
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(parts[1].length / 4) * 4, '=')

  return base64
}

export const isTokenExpired = (token) => {
  if (!token) return true

  try {
    const payload = JSON.parse(atob(getBase64UrlPayload(token)))
    return payload.exp * 1000 < Date.now() + 60_000
  } catch {
    return true
  }
}

export const authStorage = {
  getToken() {
    return migrateLegacyValue(STORAGE_KEYS.TOKEN, LEGACY_STORAGE_KEYS.TOKEN)
  },

  getSessionMode() {
    return safeRead(STORAGE_KEYS.SESSION_MODE)
  },

  getUser() {
    return safeJsonParse(migrateLegacyValue(STORAGE_KEYS.USER, LEGACY_STORAGE_KEYS.USER))
  },

  getSchoolConfig() {
    return safeJsonParse(migrateLegacyValue(STORAGE_KEYS.SCHOOL_CONFIG, LEGACY_STORAGE_KEYS.SCHOOL_CONFIG))
  },

  saveToken(token) {
    if (!token) return
    safeWrite(STORAGE_KEYS.TOKEN, token)
    safeRemove(LEGACY_STORAGE_KEYS.TOKEN)
  },

  clearToken() {
    safeRemove(STORAGE_KEYS.TOKEN)
    safeRemove(LEGACY_STORAGE_KEYS.TOKEN)
  },

  saveSessionMode(mode) {
    if (!mode) return
    safeWrite(STORAGE_KEYS.SESSION_MODE, mode)
  },

  saveUser(user) {
    if (!user) return
    safeWrite(STORAGE_KEYS.USER, JSON.stringify(user))
    safeRemove(LEGACY_STORAGE_KEYS.USER)
  },

  saveSchoolConfig(schoolConfig) {
    if (!schoolConfig) return
    safeWrite(STORAGE_KEYS.SCHOOL_CONFIG, JSON.stringify(schoolConfig))
    safeRemove(LEGACY_STORAGE_KEYS.SCHOOL_CONFIG)
  },

  persistSession({ token, user, schoolConfig }) {
    if (token) {
      this.saveToken(token)
      this.saveSessionMode(TOKEN_SESSION)
    } else {
      this.clearToken()
      this.saveSessionMode(COOKIE_SESSION)
    }

    this.saveUser(user)
    if (schoolConfig) this.saveSchoolConfig(schoolConfig)
  },

  clearAll() {
    Object.values(STORAGE_KEYS).forEach(safeRemove)
    Object.values(LEGACY_STORAGE_KEYS).forEach(safeRemove)
  },

  loadSession() {
    const token = this.getToken()
    const sessionMode = this.getSessionMode()
    const user = this.getUser()
    const schoolConfig = this.getSchoolConfig()

    if (!user) {
      this.clearAll()
      return { user: null, token: null, schoolConfig: null, sessionMode: null, isAuthenticated: false }
    }

    if (token) {
      if (isTokenExpired(token)) {
        this.clearAll()
        return { user: null, token: null, schoolConfig: null, sessionMode: null, isAuthenticated: false }
      }

      return { user, token, schoolConfig, sessionMode: TOKEN_SESSION, isAuthenticated: true }
    }

    if (sessionMode === COOKIE_SESSION) {
      return { user, token: null, schoolConfig, sessionMode, isAuthenticated: true }
    }

    if (!token) {
      this.clearAll()
      return { user: null, token: null, schoolConfig: null, sessionMode: null, isAuthenticated: false }
    }

    return { user: null, token: null, schoolConfig: null, sessionMode: null, isAuthenticated: false }
  },
}
